import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { uploadImage, updateUser } from '../services/api';
import { User } from '../types/User';
import { useAuth } from '../context/AuthContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface ProfileFormData {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phoneNumber: string;
  imageUrl?: string | File;
  imageKey?: string;
}

const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, profileUpdate } = useAuth();
  const [preview, setPreview] = useState<string | any>(user?.imageUrl);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phoneNumber: '',
    imageUrl: '',
  });

  useEffect(() => {
    if (user) {
      setPreview(user.imageUrl);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        username: user.username || '',
        phoneNumber: user.phoneNumber || '',
        imageUrl: user.imageUrl || '',
      });
    }
  }, [user]);

  const validateImage = (file: File): boolean => {
    // Check if file is an image
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      setImageError('Please upload a valid image file (JPG or PNG)');
      return false;
    }
    setImageError(null);
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhoneChange = (value: string) => {
    setFormData({
      ...formData,
      phoneNumber: value,
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        username: user.username || '',
        phoneNumber: user.phoneNumber || '',
        imageUrl: user.imageUrl || '',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // TODO: Implement profile update logic
      if(formData.imageUrl && typeof formData.imageUrl === 'object' && (formData.imageUrl as any) instanceof File) {
        try {
          const formImageData = new FormData();
          formImageData.append('image', formData.imageUrl); // the file
          const { data: s3Key } = await uploadImage(formImageData);
          formData.imageUrl = s3Key.url;
        } catch (error) {
          console.log("Error", error) 
        }
      }
      const { data } = await updateUser(formData, user?.id as any);
      const updatedUser: User = {
        id: data.id,
        username: data.username,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        imageUrl: data.imageUrl,
      };
      profileUpdate(updatedUser);
      setSuccess('Profile updated successfully');
      setIsEditing(false);
    } catch (err: any) {
      if(err?.response?.data?.errors) {
        err.response.data.errors.forEach((error: any) => {
          setError(error.msg);
        });
      } else {
        setError(err.response.data.message);
      }
      console.log({err});
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type if onFileSelect is provided
    if (validateImage && !validateImage(file)) {
      return;
    }

    // Show loading state
    setImageLoading(true);

    // Create a preview URL
    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    // In a real application, you would upload the file to a server here
    // For now, we'll simulate an upload with a timeout
    setTimeout(() => {
      const file = event.target.files?.[0];
      setFormData({...formData, imageUrl: file});
      setImageLoading(false);
    }, 1000);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography component="h1" variant={isMobile ? "h5" : "h4"} gutterBottom>
              Profile
            </Typography>
            {!isEditing && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ minWidth: '120px' }}
              >
                {isMobile ? 'Edit' : 'Edit Profile'}
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {imageLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Uploading image...
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12} display="flex" justifyContent="center">
                <Box sx={{ position: 'relative' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                  <Avatar
                    src={preview}
                    alt={`${formData.firstName} ${formData.lastName}`}
                    sx={{
                      width: 120,
                      height: 120,
                      border: `4px solid ${theme.palette.primary.main}`,
                    }}
                  />
                  {isEditing && (
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                      onClick={handleClick}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>

              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Grid>

              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Grid>

              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Grid>

              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </Grid>

              {/* @ts-ignore - Ignoring the item prop type issue */}
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <PhoneInput
                    country={'ng'}
                    value={formData.phoneNumber}
                    onChange={handlePhoneChange}
                    disabled={!isEditing}
                    inputStyle={{
                      width: '100%',
                      height: '56px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      backgroundColor: isEditing ? 'inherit' : 'rgba(0, 0, 0, 0.06)',
                    }}
                    buttonStyle={{
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRight: 'none',
                      borderRadius: '4px 0 0 4px',
                      backgroundColor: isEditing ? 'inherit' : 'rgba(0, 0, 0, 0.06)',
                    }}
                    dropdownStyle={{
                      width: '300px',
                    }}
                    searchStyle={{
                      width: '90%',
                      margin: '10px auto',
                    }}
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    searchNotFound="Country not found"
                    preferredCountries={['us', 'gb', 'ca']}
                  />
                </Box>
              </Grid>

              {isEditing && (
                // @ts-ignore - Ignoring the item prop type issue
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={isLoading}
                    >
                      Save
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile; 